/* eslint-disable max-len */
import React, { useState } from 'react';
import { connect } from 'react-redux';
import './AddNewApiOkxModal.scss';
import { Button, Form, InputGroup } from 'react-bootstrap';
import {
  apiKeyRequest
} from '../../store/apiKey/apiKeyActions';
import { 
  Key, 
  Lock, 
  Save, 
  AlertCircle, 
  CheckCircle, 
  Eye, 
  EyeOff,
  Shield,
  Info,
  Hash
} from 'lucide-react';

function AddNewApiOkx({handleClose, ...props }) {
  const {
    apiKeyRequest,
  } = props;

  const apiKeyRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  const apiSecretRegex = /^[0-9a-fA-F]{32}$/;
  const passPhaseRegex = /^[a-zA-Z0-9!@#$%^&*()_+[\]{};':"\\|,.<>\/?`~\-]{1,32}$/;

  const [errors, setErrors] = useState({ apiKey: '', apiSecret: '', passPhase: '' });
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [passPhase, setApiPassPhase] = useState('');
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [showPassPhase, setShowPassPhase] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const validateInputs = () => {
    const newErrors = { apiKey: '', apiSecret: '', passPhase: '' };
  
    if (!apiKeyRegex.test(apiKey)) {
      newErrors.apiKey = 'API Key must be a valid UUID (e.g., b4877972-868f-40e3-8a9c-bb813e56e4e6).';
    }
  
    if (!apiSecretRegex.test(apiSecret)) {
      newErrors.apiSecret = 'API Secret must be a 32-character hexadecimal string (e.g., 9F356E100D65392BA6FCD0905AE39DDB).';
    }
  
    if (!passPhaseRegex.test(passPhase)) {
      newErrors.passPhase = 'Passphrase must be between 1 and 32 characters, including special characters.';
    }
  
    setErrors(newErrors);
    return !newErrors.apiKey && !newErrors.apiSecret && !newErrors.passPhase;
  };
  
  const addApiKey = async (e) => {
    e.preventDefault();
    if (!validateInputs()) return;
    
    setIsLoading(true);
    try {
      const payload = {
        api_key: apiKey,
        api_secret: apiSecret,
        exchange: 'okx',
        pass_phase: passPhase,
      };
      await apiKeyRequest(payload);
      handleClose();
    } catch (err) {
      console.error('API Key addition failed:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modern-api-form okx-form">
      {/* Security Notice */}
      <div className="security-notice">
        <Shield size={20} className="notice-icon" />
        <div className="notice-content">
          <h6>Secure OKX Connection</h6>
          <p>Your API credentials are encrypted and stored securely</p>
        </div>
      </div>

      <form onSubmit={addApiKey} className="api-form">
        {/* API Key Field */}
        <div className="form-group">
          <label className="form-label">
            <Key size={16} />
            API Key
          </label>
          <div className="input-wrapper">
            <InputGroup className="modern-input-group">
              <Form.Control
                type="text"
                placeholder="Enter your OKX API Key (UUID format)"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                isInvalid={!!errors.apiKey}
                className="modern-form-control"
              />
              {apiKey && !errors.apiKey && (
                <div className="input-success-icon">
                  <CheckCircle size={16} />
                </div>
              )}
            </InputGroup>
            {errors.apiKey && (
              <div className="error-message">
                <AlertCircle size={14} />
                <span>{errors.apiKey}</span>
              </div>
            )}
          </div>
        </div>

        {/* API Secret Field */}
        <div className="form-group">
          <label className="form-label">
            <Lock size={16} />
            API Secret
          </label>
          <div className="input-wrapper">
            <InputGroup className="modern-input-group">
              <Form.Control
                type={showApiSecret ? "text" : "password"}
                placeholder="Enter your OKX API Secret (32 hex chars)"
                value={apiSecret}
                onChange={(e) => setApiSecret(e.target.value)}
                isInvalid={!!errors.apiSecret}
                className="modern-form-control"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowApiSecret(!showApiSecret)}
              >
                {showApiSecret ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              {apiSecret && !errors.apiSecret && (
                <div className="input-success-icon">
                  <CheckCircle size={16} />
                </div>
              )}
            </InputGroup>
            {errors.apiSecret && (
              <div className="error-message">
                <AlertCircle size={14} />
                <span>{errors.apiSecret}</span>
              </div>
            )}
          </div>
        </div>

        {/* Passphrase Field */}
        <div className="form-group">
          <label className="form-label">
            <Hash size={16} />
            Passphrase
          </label>
          <div className="input-wrapper">
            <InputGroup className="modern-input-group">
              <Form.Control
                type={showPassPhase ? "text" : "password"}
                placeholder="Enter your OKX API Passphrase"
                value={passPhase}
                onChange={(e) => setApiPassPhase(e.target.value)}
                isInvalid={!!errors.passPhase}
                className="modern-form-control"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassPhase(!showPassPhase)}
              >
                {showPassPhase ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              {passPhase && !errors.passPhase && (
                <div className="input-success-icon">
                  <CheckCircle size={16} />
                </div>
              )}
            </InputGroup>
            {errors.passPhase && (
              <div className="error-message">
                <AlertCircle size={14} />
                <span>{errors.passPhase}</span>
              </div>
            )}
          </div>
        </div>

        {/* Info Box */}
        <div className="info-box">
          <Info size={16} className="info-icon" />
          <div className="info-content">
            <h6>OKX API Requirements:</h6>
            <ul>
              <li>API Key: UUID format (8-4-4-4-12 characters)</li>
              <li>Secret Key: 32-character hexadecimal string</li>
              <li>Passphrase: Custom phrase (1-32 characters)</li>
            </ul>
            <p className="info-note">
              Enable trading permissions when creating your API key in OKX settings.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="form-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            className="cancel-btn"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="save-btn okx-btn"
            disabled={isLoading || !apiKey || !apiSecret || !passPhase}
          >
            <Save size={16} />
            {isLoading ? 'Saving...' : 'Save OKX API'}
          </Button>
        </div>
      </form>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    // Add any required state here
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    apiKeyRequest: (creds) => {
      dispatch(apiKeyRequest(creds));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AddNewApiOkx);